import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpFromLine, ExternalLink, X } from "lucide-react";
import { api, CURRENCY_META, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const COINS = ["BTC", "ETH"];
const EXPLORER = { BTC: "https://mempool.space/testnet4/tx/", ETH: "https://sepolia.etherscan.io/tx/" };
const STATUS_STYLE = {
  pending: "text-amber-400 border-amber-500/30",
  approved: "text-cyan-300 border-cyan-500/30",
  paid: "text-emerald-400 border-emerald-500/30",
  rejected: "text-rose-400 border-rose-500/30",
  cancelled: "text-zinc-500 border-white/10",
};

export const StatusChip = ({ status }) => (
  <span className={`chip uppercase tracking-wider text-[10px] ${STATUS_STYLE[status] || ""}`} data-testid={`status-${status}`}>{status}</span>
);

const History = ({ rows, onCancel }) => (
  <div className="panel overflow-hidden" data-testid="withdraw-history">
    {rows.length === 0 ? (
      <div className="p-8 text-center text-sm text-zinc-500" data-testid="withdraw-history-empty">No withdrawal requests yet.</div>
    ) : (
      rows.map((w) => (
        <div key={w.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/[0.04] text-sm" data-testid={`withdraw-row-${w.id}`}>
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: CURRENCY_META[w.currency].color }} />
          <div className="flex-1 min-w-0">
            <div className="font-mono-num">{formatAmount(w.amount, w.currency)} {w.currency}</div>
            <div className="text-[11px] text-zinc-500 font-mono-num truncate">{w.address}</div>
            {w.reason && <div className="text-[11px] text-rose-400">{w.reason}</div>}
          </div>
          <div className="text-[11px] text-zinc-500 font-mono-num hidden sm:block">{new Date(w.created_at).toLocaleString()}</div>
          <StatusChip status={w.status} />
          {w.status === "paid" && w.txid && !w.txid.startsWith("manual") && (
            <a href={EXPLORER[w.currency] + w.txid} target="_blank" rel="noreferrer" className="text-zinc-500 hover:text-amber-400"><ExternalLink size={14} /></a>
          )}
          {w.status === "pending" && (
            <button onClick={() => onCancel(w.id)} className="btn-ghost rounded-lg p-2 text-rose-400" data-testid={`withdraw-cancel-${w.id}`}><X size={14} /></button>
          )}
        </div>
      ))
    )}
  </div>
);

export default function Withdraw() {
  const { user, setUser } = useAuth();
  const [coin, setCoin] = useState("BTC");
  const [address, setAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [limits, setLimits] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const bal = user?.balances?.[coin] ?? 0;
  const min = limits?.min?.[coin];
  const load = useCallback(() => api.get("/withdrawals/me").then((r) => setRows(r.data)), []);

  useEffect(() => { api.get("/withdrawals/limits").then((r) => setLimits(r.data)); load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/withdrawals", { currency: coin, address: address.trim(), amount: Number(amount) });
      setUser((u) => ({ ...u, balances: data.balances }));
      toast.success("Withdrawal requested — awaiting admin approval");
      setAmount("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (id) => {
    try {
      const { data } = await api.post(`/withdrawals/${id}/cancel`);
      setUser((u) => ({ ...u, balances: data.balances }));
      toast.success("Withdrawal cancelled and refunded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <div className="flex flex-col gap-8 max-w-4xl" data-testid="withdraw-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Withdraw</h1>
        <p className="text-zinc-500 text-sm mt-1">Requests are reviewed by an operator and paid out manually on testnet.</p>
      </div>

      <form onSubmit={submit} className="panel p-6 flex flex-col gap-5 fade-up" data-testid="withdraw-form">
        <div className="flex items-center gap-2">
          {COINS.map((c) => (
            <button type="button" key={c} onClick={() => { setCoin(c); setAddress(""); }}
              className={`px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 ${coin === c ? "btn-cyan" : "btn-ghost"}`}
              data-testid={`withdraw-coin-${c}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: CURRENCY_META[c].color }} /> {c}
            </button>
          ))}
          <div className="ml-auto text-xs text-zinc-500">
            Available <span className="font-mono-num text-white" data-testid="withdraw-available">{formatAmount(bal, coin)}</span> {coin}
          </div>
        </div>

        <label className="flex flex-col gap-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{limits?.networks?.[coin] || coin} address</span>
          <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder={coin === "BTC" ? "tb1q…" : "0x…"} required
            className="panel w-full px-4 py-3 outline-none font-mono-num text-sm" style={{ background: "#0F0F11" }} data-testid="withdraw-address-input" />
        </label>

        <label className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Amount</span>
            <button type="button" onClick={() => setAmount(String(bal))} className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-amber-400" data-testid="withdraw-max">Max</button>
          </div>
          <div className="panel flex items-center gap-2 px-4 py-3" style={{ background: "#0F0F11" }}>
            <input type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} required
              className="flex-1 bg-transparent outline-none font-mono-num text-right text-lg" data-testid="withdraw-amount-input" />
            <span className="text-xs text-zinc-500 font-mono-num">{coin}</span>
          </div>
          <span className="text-[11px] text-zinc-500">Minimum <span className="font-mono-num text-zinc-300" data-testid="withdraw-min">{min ?? "—"}</span> {coin} · balance is locked while the request is open.</span>
        </label>

        <button type="submit" disabled={busy || !user} className="btn-gold rounded-xl py-4 font-display font-bold text-base tracking-wide flex items-center justify-center gap-2" data-testid="withdraw-submit">
          <ArrowUpFromLine size={16} /> {busy ? "Submitting…" : "Request withdrawal"}
        </button>
      </form>

      <section>
        <h2 className="font-display text-lg font-black tracking-tighter mb-3">Withdrawal history</h2>
        <History rows={rows} onCancel={cancel} />
      </section>
    </div>
  );
}
